import { useDraggable } from '@dnd-kit/core'
import { CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import UrlCard from '../UrlCard'
import UrlListItem from '../UrlListItem'
import MobileUrlCard from './MobileUrlCard'
import type {
  BookmarkedUrl,
  BookmarkViewMode,
} from '../../../../types/bookmark'
import { useTranslation } from 'react-i18next'

interface BookmarkItemProps {
  url: BookmarkedUrl
  viewMode: BookmarkViewMode
  isMobile: boolean
  isSelected: boolean
  isSelectionMode: boolean
  onToggleSelect: (urlId: string) => void
  onUpdate: (url: BookmarkedUrl) => void
  onDelete: (urlId: string) => void
  onCopySuccess: (text: string) => void
  onTagClick?: (tagId: string) => void
  /** 상세 패널 열기 */
  onOpenDetail?: (urlId: string) => void
}

/**
 * 목록의 한 항목.
 * 뷰 모드·화면 크기에 따라 표현을 고르고, 선택 체크박스와 드래그를 얹는다.
 * 드래그는 데스크톱에서만 활성화한다 (모바일은 스크롤과 충돌).
 */
export default function BookmarkItem({
  url,
  viewMode,
  isMobile,
  isSelected,
  isSelectionMode,
  onToggleSelect,
  onUpdate,
  onDelete,
  onCopySuccess,
  onTagClick,
  onOpenDetail,
}: BookmarkItemProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: url._id, disabled: isMobile || isSelectionMode })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined

  const content =
    viewMode === 'list' ? (
      <UrlListItem
        url={url}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onCopySuccess={onCopySuccess}
        onTagClick={onTagClick}
      />
    ) : isMobile ? (
      <MobileUrlCard
        url={url}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onCopySuccess={onCopySuccess}
        onTagClick={onTagClick}
      />
    ) : (
      <UrlCard
        url={url}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onCopySuccess={onCopySuccess}
        onTagClick={onTagClick}
      />
    )

  const dragProps =
    isMobile || isSelectionMode ? {} : { ...attributes, ...listeners }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      className={`group relative rounded-xl transition ${
        isSelected
          ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-gray-900'
          : ''
      } ${isMobile || isSelectionMode ? '' : 'cursor-grab active:cursor-grabbing'}`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={isSelected}
        aria-label={t('bookmark.bulk.selectItem', {
          name: url.title || url.shortCode,
        })}
        onClick={event => {
          event.stopPropagation()
          onToggleSelect(url._id)
        }}
        className={`absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border transition ${
          isSelected
            ? 'border-primary-600 bg-primary-600 text-white'
            : 'border-gray-300 bg-white/90 text-transparent opacity-0 hover:border-primary-400 focus:opacity-100 group-hover:opacity-100 dark:border-gray-600 dark:bg-gray-800/90'
        } ${isSelectionMode ? 'opacity-100' : ''}`}
      >
        <CheckIcon className="h-4 w-4" />
      </button>

      {onOpenDetail && (
        <button
          type="button"
          aria-label={t('bookmark.detail.open')}
          onClick={event => {
            event.stopPropagation()
            onOpenDetail(url._id)
          }}
          className="absolute right-2 top-2 z-10 grid h-6 w-6 place-items-center rounded-md border border-gray-300 bg-white/90 text-gray-500 opacity-0 transition hover:border-primary-400 hover:text-primary-600 focus:opacity-100 group-hover:opacity-100 dark:border-gray-600 dark:bg-gray-800/90"
        >
          <InformationCircleIcon className="h-4 w-4" />
        </button>
      )}

      {content}
    </div>
  )
}
